import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  BookOpen,
  Building2,
  Calculator,
  ChevronDown,
  FileSignature,
  FileText,
  Handshake,
  Flag,
  FileSpreadsheet,
  HardDrive,
  KeyRound,
  LayoutGrid,
  LogOut,
  Menu,
  Settings,
  Table2,
  Users,
  GraduationCap,
  type LucideIcon,
} from "lucide-react";

import type { UserRole } from "@/auth/lucia";
import { canDocflow } from "@/auth/portal";
import { countOpenComplaints } from "@/braining/complaints.functions";
import { onBadge } from "@/braining/badge-count";
import { loadLearning } from "@/braining/learning.functions";
import type { BlocksLibrary } from "@/braining/mocks";
import { useEstimates } from "@/braining/store";
import { ReportSelection } from "./report-selection";
import { logoutFn, type CurrentUser } from "@/server/auth.functions";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { BrainingLogo } from "@/braining/components/braining-logo";

/** Роли словами агентства: «rop» в панели читалось бы как опечатка. */
const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Администратор",
  rop: "Руководитель",
  manager: "Менеджер",
  docs: "Документы",
  // В панели сотрудника подрядчика не бывает; запись — для полноты типа.
  contractor: "Партнёр",
};

async function signOut() {
  await logoutFn();
  // Полная перезагрузка, а не router.navigate: сессионная кука сменилась, и
  // весь загруженный стор принадлежит человеку, который только что вышел.
  window.location.href = "/login";
}

/**
 * Навигация построена вокруг модулей, а не плоского списка страниц: у агентства
 * в планах порядка десяти модулей, и «Калькуляторы» — только первый. Добавление
 * следующего сводится к записи в этом массиве.
 */
interface NavItem {
  to:
    | "/"
    | "/library"
    | "/ratecard"
    | "/learning"
    | "/complaints"
    | "/templates"
    | "/settings"
    | "/google"
    | "/notifications"
    | "/users"
    | "/contractors"
    | "/deals"
    | "/entities"
    | "/regulations";
  label: string;
  icon: LucideIcon;
  exact?: boolean;
  /** пункт только для администратора — например, управление людьми */
  adminOnly?: boolean;
  /** пункт для тех, кто отвечает за справочники: администратор и руководитель */
  leadOnly?: boolean;
  /** пункт документооборота: ещё и менеджер по документам */
  docflow?: boolean;
  /** счётчик справа: сколько ждёт решения */
  badge?: number;
}

interface NavModule {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  /** раздел не для менеджера по документам — сметы ему не нужны */
  notForDocs?: boolean;
}

/**
 * Названия — из лексики агентства: сам артефакт они называют калькулятором,
 * а «сметы» для них область работы целиком. Наши внутренние термины сюда не
 * протаскиваем.
 */
const MODULES: NavModule[] = [
  {
    id: "estimates",
    label: "Сметы",
    icon: FileSpreadsheet,
    notForDocs: true,
    items: [
      { to: "/", label: "Калькуляторы", icon: Calculator, exact: true },
      { to: "/library", label: "Блоки работ", icon: LayoutGrid },
      { to: "/ratecard", label: "Рейткарты", icon: Table2 },
      // Отдельным пунктом, а не вкладкой в справочнике: справочник открывают
      // раз в месяц, и вкладку в нём никто бы не открыл. Счётчик здесь и есть
      // то, ради чего раздел заведён — он единственный говорит, что система
      // чему-то научилась.
      { to: "/learning", label: "Обучение", icon: GraduationCap, leadOnly: true },
    ],
  },
  {
    // Документооборот с подрядчиками (notes/plan-docflow.md). Ведут его
    // администратор, руководитель и менеджер по документам: здесь паспорта и
    // реквизиты людей, и менеджеру смет они не нужны — RLS говорит то же.
    id: "docs",
    label: "Документы",
    icon: FileSignature,
    items: [
      { to: "/deals", label: "Комплекты документов", icon: FileSignature, docflow: true },
      { to: "/contractors", label: "Подрядчики", icon: Handshake, docflow: true },
      { to: "/entities", label: "Юрлица агентства", icon: Building2, docflow: true },
      { to: "/templates", label: "Шаблоны", icon: FileText, docflow: true },
    ],
  },
  {
    // Регламенты (созвон с Викой 26.10): нужны всем — и тому, кто считает
    // сметы, и тому, кто ведёт документы.
    id: "regulations",
    label: "Регламенты",
    icon: BookOpen,
    items: [{ to: "/regulations", label: "Регламенты", icon: BookOpen }],
  },
  {
    id: "settings",
    label: "Настройки",
    icon: Settings,
    // Два отдельных пункта, а не карточки на одном экране: ключ AI и доступ к
    // Drive — разные подключения, и рядом они путались.
    items: [
      { to: "/settings", label: "Ключи", icon: KeyRound },
      { to: "/google", label: "Google Drive", icon: HardDrive },
      { to: "/notifications", label: "Уведомления", icon: Bell, adminOnly: true },
      // Жалобы — в настройках, а не рядом с калькуляторами: это разбор работы
      // системы, и открывают его не в ходе сметы, а когда садятся чинить.
      { to: "/complaints", label: "Жалобы", icon: Flag, leadOnly: true },
      { to: "/users", label: "Пользователи", icon: Users, adminOnly: true },
    ],
  },
];

export function AppShell({
  user,
  children,
}: {
  user: CurrentUser | null;
  children: React.ReactNode;
}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const { adoptLibrary } = useEstimates();
  const pending = usePendingLearning(user, path, adoptLibrary);
  const complaints = useOpenComplaints(user, path);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Панель фиксирована: длинные сметы прокручиваются, навигация — нет */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-sidebar lg:flex">
        <SidebarContent path={path} user={user} pending={pending} complaints={complaints} />
      </aside>

      {/* Узкие экраны: панель уезжает в выдвижную шторку */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-sidebar px-4 lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger
            className="text-sidebar-accent-foreground/80 hover:text-sidebar-accent-foreground"
            aria-label="Меню"
          >
            <Menu className="size-5" />
          </SheetTrigger>
          <SheetContent side="left" className="w-60 border-0 bg-sidebar p-0">
            <SheetTitle className="sr-only">Навигация</SheetTitle>
            <SidebarContent
              path={path}
              user={user}
              pending={pending}
              complaints={complaints}
              onNavigate={() => setOpen(false)}
            />
          </SheetContent>
        </Sheet>
        <Wordmark />
      </header>

      <main className="px-4 py-6 sm:px-6 lg:py-8 lg:pl-[calc(15rem+2rem)] lg:pr-8">
        <div className="mx-auto max-w-[1400px]">{children}</div>
      </main>

      {/* Жалоба по выделенному тексту — на любом экране, один раз на всё
          приложение. */}
      <ReportSelection />
    </div>
  );
}

/**
 * Сколько предложений ждёт решения — цифра рядом с «Обучением».
 *
 * Раз за сессию, а не на каждый переход: раздел не про срочность, а про то,
 * чтобы о нём вообще вспомнили. Заодно этот же запрос применяет то, что
 * система вправе делать сама, — поэтому он и живёт в панели, а не на самом
 * экране: ждать, пока кто-нибудь откроет «Обучение», значит не применить
 * никогда.
 *
 * На самом экране «Обучения» не зовём: он спрашивает то же самое сам, а два
 * одновременных автоприменения перезаписали бы друг друга.
 */
function usePendingLearning(
  user: CurrentUser | null,
  path: string,
  adopt: (next: BlocksLibrary) => void,
): number | undefined {
  const [pending, setPending] = useState<number | undefined>(undefined);
  const lead = user?.role === "admin" || user?.role === "rop";
  const onLearning = path.startsWith("/learning");

  // Точное число говорит сам экран — панель его слушает и не пересчитывает.
  useEffect(() => onBadge((kind, n) => kind === "learning" && setPending(n)), []);

  useEffect(() => {
    if (!lead) return;
    // На самом экране считать незачем: он сейчас скажет своё число. В
    // остальных случаях считаем при заходе и при переходе между экранами —
    // иначе принятое предложение остаётся в цифре до перезагрузки.
    if (onLearning) return;
    let alive = true;
    loadLearning()
      .then((s) => {
        if (!alive) return;
        setPending(s.suggestions.length);
        // Автоприменение могло изменить справочник: подставляем его версию,
        // иначе правка с экрана блоков уедет со старой отметкой.
        if (s.library) adopt(s.library);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, onLearning]);

  return pending;
}

/**
 * Сколько жалоб ждёт разбора — цифра рядом с «Жалобами».
 *
 * Та же причина, что у счётчика обучения, только острее: на прогоне 21.09
 * девять жалоб за полчаса пролежали незамеченными до встречи. Телеграм зовёт
 * того, кто чинит, а цифра в панели напоминает тем, кто уже внутри.
 */
function useOpenComplaints(user: CurrentUser | null, path: string): number | undefined {
  const [open, setOpen] = useState<number | undefined>(undefined);
  const lead = user?.role === "admin" || user?.role === "rop";
  const onComplaints = path.startsWith("/complaints");

  useEffect(() => onBadge((kind, n) => kind === "complaints" && setOpen(n)), []);

  useEffect(() => {
    if (!lead) return;
    // То же, что у счётчика обучения: на своём экране цифру говорит экран.
    if (onComplaints) return;
    let alive = true;
    countOpenComplaints()
      .then((r) => alive && setOpen(r.open))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [lead, onComplaints]);

  return open;
}

function visibleTo(item: NavItem, user: CurrentUser | null): boolean {
  if (item.adminOnly && user?.role !== "admin") return false;
  if (item.leadOnly && user?.role !== "admin" && user?.role !== "rop") return false;
  if (item.docflow && !canDocflow(user?.role)) return false;
  return true;
}

function isActive(item: NavItem, path: string): boolean {
  return item.exact ? path === item.to : path.startsWith(item.to);
}

const COLLAPSED_KEY = "braining.nav.collapsed";

/**
 * Свёрнутые разделы — удобство одного браузера, а не настройка учётной
 * записи: хранится в localStorage. Читается после монтирования, чтобы
 * серверная отрисовка и первая клиентская совпали.
 */
function useCollapsedModules(): [Set<string>, (id: string) => void] {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(COLLAPSED_KEY);
      if (raw) setCollapsed(new Set(JSON.parse(raw) as string[]));
    } catch {
      // приватное окно или битая запись — просто всё развёрнуто
    }
  }, []);
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // не запомнится — не беда
      }
      return next;
    });
  return [collapsed, toggle];
}

function SidebarContent({
  path,
  user,
  pending,
  complaints,
  onNavigate,
}: {
  path: string;
  user: CurrentUser | null;
  pending?: number;
  complaints?: number;
  onNavigate?: () => void;
}) {
  const [collapsed, toggle] = useCollapsedModules();
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5">
        <Wordmark />
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3">
        {MODULES.map((module) => {
          if (module.notForDocs && user?.role === "docs") return null;
          const items = module.items.filter((item) => visibleTo(item, user));
          // Раздел, где человеку нечего открыть, не показываем вовсе: у
          // менеджера «Документы» были бы пустым заголовком.
          if (!items.length) return null;
          const shut = collapsed.has(module.id);
          const holdsActive = items.some((item) => isActive(item, path));
          return (
            <section key={module.id}>
              {/* Раздел сворачивается: Даше, которая ведёт документы, сметы
                не нужны, а менеджеру не нужны документы. Что свёрнуто,
                помнит браузер. */}
              <button
                type="button"
                onClick={() => toggle(module.id)}
                aria-expanded={!shut}
                aria-label={`Раздел «${module.label}»`}
                className="group flex w-full items-center gap-2 rounded-md px-2 pb-2 text-left"
              >
                <module.icon className="size-3.5 text-sidebar-foreground" />
                <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-sidebar-foreground">
                  {module.label}
                </span>
                {shut && holdsActive ? (
                  <span className="size-1.5 rounded-full bg-sidebar-primary" aria-hidden />
                ) : null}
                <ChevronDown
                  className={cn(
                    "ml-auto size-3.5 text-sidebar-foreground opacity-60 transition-transform group-hover:opacity-100",
                    shut && "-rotate-90",
                  )}
                />
              </button>

              {shut ? null : (
                <div className="space-y-0.5">
                  {items.map((item) => {
                    const badge =
                      item.to === "/learning"
                        ? pending
                        : item.to === "/complaints"
                          ? complaints
                          : undefined;
                    const active = isActive(item, path);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={onNavigate}
                        className={cn(
                          "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                          active
                            ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                            : "text-sidebar-accent-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                        )}
                      >
                        <item.icon
                          className={cn(
                            "size-4 shrink-0",
                            active ? "text-sidebar-primary" : "text-sidebar-foreground",
                          )}
                        />
                        {item.label}
                        {badge ? (
                          <span className="ml-auto rounded-full bg-sidebar-primary px-1.5 py-0.5 text-[11px] font-medium text-sidebar">
                            {badge}
                          </span>
                        ) : null}
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        {/* «Кто это зашёл?» — вопрос с демо. Почта и роль видны всегда, иначе
            в общей переговорке не понять, под кем открыт экран. */}
        {user && (
          <div className="flex items-center gap-2 px-1">
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs text-sidebar-accent-foreground">{user.email}</div>
              <div className="text-[11px] text-sidebar-foreground">{ROLE_LABEL[user.role]}</div>
            </div>
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label="Выйти"
              className="shrink-0 rounded-md p-1.5 text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Логотип Braining Group с их сайта; ведёт на список калькуляторов. */
function Wordmark() {
  return (
    <Link to="/" aria-label="Braining Group — калькуляторы" className="flex items-center">
      <BrainingLogo className="h-8 w-auto text-sidebar-accent-foreground" />
    </Link>
  );
}
